import {
    format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
    subMonths, subWeeks, subDays,
} from 'date-fns';

export type QuickPeriod =
    | 'today' | 'yesterday'
    | 'last7Days' | 'last30Days' | 'last90Days'
    | 'weekToDate' | 'monthToDate'
    | 'lastWeek' | 'lastMonth';

export const QUICK_PERIODS: { key: QuickPeriod; label: string; group: 'recent' | 'calendar' }[] = [
    { key: 'today',        label: 'Today',         group: 'recent'   },
    { key: 'yesterday',    label: 'Yesterday',     group: 'recent'   },
    { key: 'last7Days',    label: 'Last 7 days',   group: 'recent'   },
    { key: 'last30Days',   label: 'Last 30 days',  group: 'recent'   },
    { key: 'last90Days',   label: 'Last 90 days',  group: 'recent'   },
    { key: 'weekToDate',   label: 'Week to date',  group: 'calendar' },
    { key: 'monthToDate',  label: 'Month to date', group: 'calendar' },
    { key: 'lastWeek',     label: 'Last week',     group: 'calendar' },
    { key: 'lastMonth',    label: 'Last month',    group: 'calendar' },
];

const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

/**
 * Returns absolute YYYY-MM-DD start/end for a quick-date preset, evaluated against `now`.
 * PM reports never include future tasks, so end is always clamped to today.
 */
export function resolveQuickPeriod(period: QuickPeriod, now: Date): { startDate: string; endDate: string } {
    let start: Date;
    let end: Date = now;

    switch (period) {
        case 'today':       start = now; break;
        case 'yesterday':   start = subDays(now, 1); end = subDays(now, 1); break;
        case 'last7Days':   start = subDays(now, 6); break;
        case 'last30Days':  start = subDays(now, 29); break;
        case 'last90Days':  start = subDays(now, 89); break;
        case 'weekToDate':  start = startOfWeek(now, { weekStartsOn: 1 }); break;
        case 'monthToDate': start = startOfMonth(now); break;
        case 'lastWeek': {
            const lw = subWeeks(now, 1);
            start = startOfWeek(lw, { weekStartsOn: 1 });
            end = endOfWeek(lw, { weekStartsOn: 1 });
            break;
        }
        case 'lastMonth': {
            const lm = subMonths(now, 1);
            start = startOfMonth(lm);
            end = endOfMonth(lm);
            break;
        }
    }

    if (end.getTime() > now.getTime()) end = now;
    if (start.getTime() > end.getTime()) start = end;

    return { startDate: fmt(start), endDate: fmt(end) };
}

/** Given a YYYY-MM-DD start/end pair, return the matching quick preset or null. */
export function matchQuickPeriod(startDate: string, endDate: string, now: Date): QuickPeriod | null {
    if (!startDate || !endDate) return null;
    for (const { key } of QUICK_PERIODS) {
        const r = resolveQuickPeriod(key, now);
        if (r.startDate === startDate && r.endDate === endDate) return key;
    }
    return null;
}
